import { AxiosInstance, AxiosRequestConfig } from 'axios';

import {
  Company,
  HrConnect,
  Instance,
  NavigationApiModel,
  Persona,
  UserflowSignature,
  XemploStateScope,
} from '@xemplo/types';

import { DS_BASE_URL, DS_URL_PREFIX, DSApiUrl } from './directory-services.constants';
import {
  BaseRequestProps,
  DSInstanceConfig,
  PagedRequest,
  RequestWithCode,
  RequestWithId,
  RequestWithIds,
  RequestWithParams,
} from './directory-services.types';

export class DirectoryServices {
  private readonly _client: AxiosInstance;
  private readonly _baseRequestConfig: AxiosRequestConfig;
  private readonly baseUrl: string;

  constructor(config: DSInstanceConfig) {
    this._client = config.client;
    this._baseRequestConfig = {
      baseURL: `${config.baseUrl ?? DS_BASE_URL}/${DS_URL_PREFIX}`,
    };
    this.baseUrl = config.baseUrl ?? DS_BASE_URL;
  }

  public getPersonas(props?: RequestWithParams<PagedRequest>) {
    const url = DSApiUrl.getPersonasUrl(this.baseUrl);
    return this._client.get<Persona[]>(url, this.makeConfig(props));
  }

  public getInstanceById({ id, ...config }: RequestWithId) {
    const url = DSApiUrl.getInstanceByIdUrl(this.baseUrl, id);
    return this._client.get<Instance>(url, this.makeConfig(config));
  }

  public getCompaniesByInstanceId({ id, ...config }: RequestWithId) {
    const url = DSApiUrl.getCompaniesByInstanceIdUrl(this.baseUrl, id);
    return this._client.get<Company[]>(url, this.makeConfig(config));
  }

  public getInstances(props?: RequestWithParams<PagedRequest>) {
    const url = DSApiUrl.getInstancesUrl(this.baseUrl);
    return this._client.get<Instance[]>(url, this.makeConfig(props));
  }

  public getCompanyById({ id, ...config }: RequestWithId) {
    const url = DSApiUrl.getCompanyByIdUrl(this.baseUrl, id);
    return this._client.get<Company>(url, this.makeConfig(config));
  }

  public getCompanies({ ids, ...config }: RequestWithIds) {
    const promises = ids.map((id) =>
      this.getCompanyById({ id, ...config }).then((res) => res.data),
    );
    return Promise.all(promises);
  }

  public getNavigation(props?: BaseRequestProps) {
    const url = DSApiUrl.getNavigationUrl(this.baseUrl);
    return this._client.get<NavigationApiModel>(url, props);
  }

  public getUserflowSignature({ code, ...config }: RequestWithCode) {
    const url = DSApiUrl.getUserflowUrl(this.baseUrl, code);
    return this._client.get<UserflowSignature>(url, this.makeConfig(config));
  }

  public getConnectEmbeddedUrl({ code, ...config }: RequestWithCode) {
    const url = DSApiUrl.getConnectEmbeddedUrl(this.baseUrl, code);
    return this._client.get<HrConnect>(url, this.makeConfig(config));
  }

  public patchXemploState<
    TResult extends Record<string, unknown> = Record<string, unknown>,
  >(scope: XemploStateScope, data: Record<string, unknown>, signal?: AbortSignal) {
    const url = DSApiUrl.patchXemploStateUrl(this.baseUrl, scope);
    return this._client.patch<TResult>(url, data, { signal, withCredentials: true });
  }

  private readonly makeConfig = <T>(props?: RequestWithParams<T>) => {
    return { ...this._baseRequestConfig, ...props };
  };
}

export default DirectoryServices;
